import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiUser, FiUserX } from "react-icons/fi";
import { MdAccountBalance } from "react-icons/md";
import { employeeService } from "../../services/employee.service";
import EmployeeDocumentsTab from "./EmployeeDocumentsTab";
import EmployeeEducationTab from "./EmployeeEducationTab";
import EmployeePersonalInfoTab from "./EmployeePersonalInfoTab";
import EmployeeRecordTab from "./EmployeeRecordTab";
import {
  EMPLOYMENT_HISTORY_TAB,
  INSURANCE_TAB,
  JOB_DESCRIPTION_TAB,
  KPI_ITEMS_TAB,
  KPI_TAB,
  LEAVE_TAB,
  RESOURCE_TAB,
  SUPERVISOR_TAB,
  TRAINING_TAB,
} from "./employeeRecordTabConfigs";

const TABS = [
  "Basic Information",
  "Personal Info",
  "Documents",
  "Education",
  "Job Description",
  "Employment History",
  "Salary",
  "Insurance",
  "Resource",
  "Training",
  "Supervisor",
  "Leave Information",
  "Performance & KPI",
  "Resignation Details",
];

const STATUS_STYLES = {
  Active: "bg-green-100 text-green-700 border-green-200",
  "On Leave": "bg-orange-100 text-orange-600 border-orange-200",
  Inactive: "bg-red-100 text-red-600 border-red-200",
};

const STATUS_DOT = {
  Active: "bg-green-500",
  "On Leave": "bg-orange-400",
  Inactive: "bg-red-500",
};

function initialsOf(name) {
  return name
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function InfoRow({ label, value }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-3 py-2 text-xs @xl:grid-cols-[minmax(0,140px)_minmax(0,1fr)]">
      <dt className="text-gray-600">{label}</dt>
      <dd className="break-words font-medium text-gray-900">{value}</dd>
    </div>
  );
}

function EmployeeDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const parts = pathname.split("/");
  const companyBase = `/${parts[1]}`;
  const moduleBase = `${companyBase}/Human-Resource`;

  useEffect(() => {
    document.title = "Employee Details";
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    employeeService
      .getEmployee(id)
      .then((result) => {
        if (!cancelled) setRecord(result);
      })
      .catch((loadError) => {
        if (cancelled) return;
        setRecord(null);
        setError(loadError?.message || "Unable to load employee details.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading || error || !record) {
    return (
      <div className="@container w-full">
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-gray-500">
          {loading ? "Loading employee details..." : error || "Employee not found."}{" "}
          {loading ? null : (
            <Link to={`${moduleBase}/dashboard`} className="text-blue-500">
              Back to Employee Management
            </Link>
          )}
        </div>
      </div>
    );
  }

  const employee = {
    key: record.id,
    id: record.id,
    name: record.name,
    title: record.designationName,
    salutation: record.salutationName,
    photoUrl: record.photoUrl,
    firstName: record.firstName,
    lastName: record.lastName,
    dateOfBirth: record.dateOfBirth,
    gender: record.genderName,
    nationality: record.nationalityName,
    nationalId: record.nationalId,
    email: record.email,
    phone: record.companyMobile,
    companyMobile: record.companyMobile,
    employmentDate: record.dateOfJoin,
    workArrangement: "-",
    manager: "-",
    department: record.departmentName,
    subDepartment: record.subDepartmentName,
    designation: record.designationName,
    jobLocation: record.jobLocationName,
    employeeCategory: record.employeeCategoryName,
    jobType: record.employeeTypeName,
    contractStart: record.contractStart,
    contractEnd: record.contractEnd,
    probationPeriod: record.probationPeriod,
    noticePeriod: record.noticePeriod,
    status: record.statusName,
  };

  const leftColumn = [
    ["Employee ID", employee.id],
    ["Title", employee.salutation],
    ["First Name", employee.firstName],
    ["Last Name", employee.lastName],
    ["Gender", employee.gender],
    ["Date of Birth", employee.dateOfBirth],
    ["Nationality", employee.nationality],
    ["National ID / Passport No.", employee.nationalId],
    ["Company Email", employee.email],
    ["Company Mobile", employee.companyMobile],
    ["Date of Employment", employee.employmentDate],
    ["Work Arrangement", employee.workArrangement],
  ];
  const resignationRows = [
    ["Resign Submit Date", "Not resigned"],
    ["Last Working Date", "-"],
    ["Reason For Leaving", "-"],
    ["Counselling Date", "-"],
    ["Counselling Comments", "-"],
    ["Employee Feedback", "-"],
    ["Salary Transfer Stop", "No"],
  ];
  const salaryLeft = [
    ["Bank", "Guaranty Trust Bank Plc"],
    ["Account Number", "0326512165"],
    ["Account Name", "Solomon Layi"],
    [
      "Bank Verification",
      <span className="inline-block rounded border border-green-200 bg-green-100 px-2.5 py-0.5 text-[11px] font-medium text-green-700">
        Verified
      </span>,
    ],
    ["Pay Mode", "Cash/Bank"],
    ["Currency", "Naira"],
  ];
  const salaryRight = [
    ["Salary", "NGN 450,000.00"],
    ["PF Deduction", "NGN 36,000.00"],
    ["TAX Deduction", "NGN 42,500.00"],
    ["Others Deduction", "NGN 5,000.00"],
    ["PF Applicable", "Yes"],
    ["TIN Number", "2054781296"],
  ];
  const rightColumn = [
    ["Department", employee.department],
    ["Sub Department", employee.subDepartment],
    ["Designation", employee.designation],
    ["Job Location", employee.jobLocation],
    ["Emp. Type", employee.jobType],
    ["Emp. Category", employee.employeeCategory],
    ["Contract Start Date", employee.contractStart],
    ["Contract End Date", employee.contractEnd],
    ["Probation Period (Month)", employee.probationPeriod],
    ["Notice Period (Month)", employee.noticePeriod],
    ["Status", employee.status],
  ];

  return (
    <div className="@container w-full min-w-0">
      <nav className="flex flex-wrap items-center gap-1 text-xs text-gray-500" aria-label="Breadcrumb">
        <Link to={`${companyBase}/dashboard`} className="hover:text-blue-500">
          Home
        </Link>
        <span>/</span>
        <Link to={`${moduleBase}/dashboard`} className="hover:text-blue-500">
          HR
        </Link>
        <span>/</span>
        <Link to={`${moduleBase}/dashboard`} className="hover:text-blue-500">
          Employee Management
        </Link>
        <span>/</span>
        <span className="font-medium text-blue-500">Employee Details</span>
      </nav>

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate(`${moduleBase}/dashboard`)}
          aria-label="Back to Employee Management"
          className="rounded p-1 text-gray-700 hover:bg-gray-100"
        >
          <FiArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Employee Details</h1>
      </div>

      <div className="mt-4 rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex flex-col gap-5 @2xl:flex-row @2xl:gap-12">
          <div className="flex flex-col items-center gap-2 @2xl:w-[150px] @2xl:shrink-0">
            {employee.photoUrl ? (
              <img src={employee.photoUrl} alt={employee.name} className="h-[70px] w-[70px] rounded-full object-cover" />
            ) : (
              <div className="flex h-[70px] w-[70px] items-center justify-center rounded-full bg-blue-100 text-xl font-semibold text-blue-600">
                {initialsOf(employee.name)}
              </div>
            )}
            <p className="text-sm font-semibold text-gray-900">{employee.title}</p>
            <span
              className={`inline-flex items-center gap-1.5 rounded border px-2.5 py-0.5 text-[11px] font-medium ${STATUS_STYLES[employee.status] || "bg-gray-100 text-gray-600 border-gray-200"}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[employee.status] || "bg-gray-400"}`} />
              {employee.status}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900">{employee.name}</p>
            <dl className="mt-2">
              <InfoRow label="Phone Number" value={employee.phone} />
              <InfoRow label="Email Address" value={employee.email} />
              <InfoRow label="Department" value={employee.department} />
              <InfoRow label="Manager" value={employee.manager} />
              <InfoRow label="Employment Date" value={employee.employmentDate} />
            </dl>
          </div>
        </div>
      </div>

      <div className="mt-5 border-b border-gray-200" role="tablist">
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 pb-2 text-xs font-medium ${
                activeTab === tab ? "border-blue-500 text-blue-500" : "border-transparent text-gray-700 hover:text-blue-500"
              }`}
            >
              {tab}
              <span className="h-2 w-2 rounded-full bg-gray-500" />
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-gray-200 bg-white p-5">
        {activeTab === "Basic Information" ? (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-500 text-white">
                  <FiUser className="h-4 w-4" />
                </span>
                <h2 className="text-sm font-semibold text-gray-900">Employee Basic Information</h2>
              </div>
              <button
                type="button"
                onClick={() => navigate(`${moduleBase}/employees/${id}/edit`)}
                className="border-b border-blue-500 text-xs font-medium text-blue-500"
              >
                Edit
              </button>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-x-10 @3xl:grid-cols-2">
              <dl>
                {leftColumn.map(([label, value]) => (
                  <InfoRow key={label} label={label} value={value} />
                ))}
              </dl>
              <dl>
                {rightColumn.map(([label, value]) => (
                  <InfoRow key={label} label={label} value={value} />
                ))}
              </dl>
            </div>
          </>
        ) : activeTab === "Personal Info" ? (
          <EmployeePersonalInfoTab key={employee.id} employeeId={employee.id} />
        ) : activeTab === "Education" ? (
          <EmployeeEducationTab key={employee.key} employeeName={employee.name} />
        ) : activeTab === "Job Description" ? (
          <EmployeeRecordTab key={employee.key} config={JOB_DESCRIPTION_TAB} employeeName={employee.name} />
        ) : activeTab === "Employment History" ? (
          <EmployeeRecordTab key={employee.key} config={EMPLOYMENT_HISTORY_TAB} employeeName={employee.name} />
        ) : activeTab === "Leave Information" ? (
          <EmployeeRecordTab key={employee.key} config={LEAVE_TAB} employeeName={employee.name} />
        ) : activeTab === "Performance & KPI" ? (
          <>
            <EmployeeRecordTab key={`${employee.key}-kpi`} config={KPI_TAB} employeeName={employee.name} />
            <div className="mt-8 border-t border-gray-100 pt-6">
              <EmployeeRecordTab key={`${employee.key}-kpi-items`} config={KPI_ITEMS_TAB} employeeName={employee.name} />
            </div>
          </>
        ) : activeTab === "Resignation Details" ? (
          <>
            <div className="flex items-center gap-3">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-500 text-white">
                <FiUserX className="h-4 w-4" />
              </span>
              <h2 className="text-sm font-semibold text-gray-900">Resignation Details</h2>
            </div>
            <dl className="mt-5">
              {resignationRows.map(([label, value]) => (
                <InfoRow key={label} label={label} value={value} />
              ))}
            </dl>
          </>
        ) : activeTab === "Supervisor" ? (
          <EmployeeRecordTab key={employee.key} config={SUPERVISOR_TAB} employeeName={employee.name} />
        ) : activeTab === "Insurance" ? (
          <EmployeeRecordTab key={employee.key} config={INSURANCE_TAB} employeeName={employee.name} />
        ) : activeTab === "Resource" ? (
          <EmployeeRecordTab key={employee.key} config={RESOURCE_TAB} employeeName={employee.name} />
        ) : activeTab === "Training" ? (
          <EmployeeRecordTab key={employee.key} config={TRAINING_TAB} employeeName={employee.name} />
        ) : activeTab === "Salary" ? (
          <>
            <div className="flex items-center gap-3">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-500 text-white">
                <MdAccountBalance className="h-4 w-4" />
              </span>
              <h2 className="text-sm font-semibold text-gray-900">Salary Information</h2>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-x-10 @3xl:grid-cols-2">
              <dl>
                {salaryLeft.map(([label, value]) => (
                  <InfoRow key={label} label={label} value={value} />
                ))}
              </dl>
              <dl>
                {salaryRight.map(([label, value]) => (
                  <InfoRow key={label} label={label} value={value} />
                ))}
              </dl>
            </div>
          </>
        ) : activeTab === "Documents" ? (
          <EmployeeDocumentsTab key={employee.key} />
        ) : (
          <div className="py-10 text-center text-sm text-gray-500">No {activeTab} information available yet.</div>
        )}
      </div>
    </div>
  );
}

export default EmployeeDetailsPage;
